import React, { useState, useRef, useEffect } from "react";
import {
  SortableList,
  SortableItem,
  arrayMove,
  useStableItemIds,
} from "../FormComponents/SortableList";
import {
  Paper,
  List,
  ListItem,
  ListItemText,
  Typography,
  Grid,
  TextField,
  Button,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Box,
  ListItemSecondaryAction,
  Tooltip,
  IconButton,
  FormControlLabel,
  Checkbox,
  Autocomplete,
} from "@mui/material";
import { ArrowDownward, Delete } from "@mui/icons-material";
import { capitalize } from "@mui/material/utils";
import axios from "axios";
import { useDebouncedCallback } from "use-debounce";
import { deepEquals } from "../../utils/misc";
import { En, Fr, I18n } from "../I18n";
import {
  paperClass,
  QuestionText,
  SupplementalText,
} from "../FormComponents/QuestionStyles";
import RequiredMark from "../FormComponents/RequiredMark";
import { validateField } from "../../utils/validate";

const TaxaTab = ({ record, updateRecord, disabled }) => {
  const getItemId = useStableItemIds("taxa");
  const { taxa = [] } = record;
  const updateTaxa = updateRecord("taxa");
  const [activeTaxa, setActiveTaxa] = useState(0);

  const [inputValue, setInputValue] = useState("");
  const [SearchValue, setSearchValue] = useState("");

  const [data, setData] = useState([]);
  const mounted = useRef(false);
  const requestInProgress = useRef(false);

  const [currentTaxa, setTaxaItems] = useState(taxa);

  if (!deepEquals(currentTaxa, taxa)) {
    setTaxaItems(taxa);
  }

  function handleAdd() {
    if (SearchValue) {
      updateTaxa(taxa.concat(SearchValue));
      setActiveTaxa(taxa.length);
      setSearchValue("");
      setInputValue("");
    }
  }

  function removeItem(itemIndex) {
    updateTaxa(taxa.filter((e, index) => index !== itemIndex));
    if (taxa.length) setActiveTaxa(taxa.length - 2);
  }

  //  removedIndex is dragStart
  //  addedIndex is dragEnd
  function onDrop({ removedIndex, addedIndex }) {
    if (removedIndex === activeTaxa) setActiveTaxa(addedIndex);
    else if (addedIndex <= activeTaxa && removedIndex > activeTaxa)
      setActiveTaxa(activeTaxa + 1);

    const reorderedContacts = arrayMove(currentTaxa, removedIndex, addedIndex);

    updateTaxa(reorderedContacts);
  }

  function getActiveTaxaDetails() {
    if (taxa[activeTaxa]) {
      const {
        rank,
        kingdom,
        phylum,
        class: Class,
        order,
        family,
        genus,
        species,
        scientificName,
        canonicalName,
        parent,
      } = taxa[activeTaxa];
      const details = {
        rank,
        kingdom,
        phylum,
        Class,
        order,
        family,
        genus,
        species,
        scientificName,
        canonicalName,
        parent,
      };
      return details;
    }
    return {};
  }

  const gbifSearch = useDebouncedCallback((searchStr) => {
    const config = {
      method: "get",
      url: "https://api.gbif.org/v1/species/suggest",
      params: {
        q: searchStr,
      },
    };
    requestInProgress.current = true;
    axios(config)
      .then((response) => {
        if (mounted.current) {
          setData([...response.data]);
        }
        requestInProgress.current = false;
      })
      .catch((error) => {
        console.error(error);
        requestInProgress.current = false;
      });
  }, 500);

  useEffect(() => {
    mounted.current = true;

    if (inputValue.length > 2 && !SearchValue && !requestInProgress.current) {
      gbifSearch(inputValue);
    }

    return () => {
      mounted.current = false;
    };
  }, [inputValue, SearchValue, gbifSearch]);

  const noTaxa = record.noTaxa && record.noTaxa !== "false";

  return (
    <Grid>
      <Paper style={paperClass}>
        <QuestionText>
          <En>Taxonomic Coverage</En>
          <Fr>Couverture taxonomique</Fr>
          <RequiredMark passes={validateField(record, "taxa")} />
          <SupplementalText>
            <I18n>
              <En>
                Please enter the metadata about the taxonomic areas covered by
                the resource.
              </En>
              <Fr>
                Veuillez saisir les métadonnées sur les domaines taxonomiques
                couverts par la ressource.
              </Fr>
            </I18n>
          </SupplementalText>
        </QuestionText>

        <FormControlLabel
          disabled={disabled}
          control={
            <Checkbox
              name="noTaxa"
              checked={record.noTaxa}
              onChange={(e) => {
                const { checked } = e.target;

                updateRecord("noTaxa")(checked);
              }}
            />
          }
          label={
            <I18n>
              <En>
                This dataset does not contain any taxonomic classification
              </En>
              <Fr>
                Ce jeu de données ne contient aucune classification taxonomique
              </Fr>
            </I18n>
          }
        />
      </Paper>
      {!noTaxa ? (
        <Paper style={paperClass}>
          <Grid container direction="column" spacing={0}>
            <Autocomplete
              inputValue={inputValue}
              onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
              }}
              getOptionLabel={(option) =>
                (option.scientificName &&
                  `${option.scientificName} (${option.canonicalName})`) ??
                option
              }
              disabled={disabled}
              // onChange={(e, keyword) =>
              //     setSelectedKeywordAltLang(translate(keyword, altLanguage))
              // }
              loading={!requestInProgress.current}
              onChange={(event, newValue) => {
                if (typeof newValue === "object") {
                  setSearchValue(newValue);
                }
              }}
              value={SearchValue}
              freeSolo
              options={data}
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={
                    <I18n>
                      <En>GBIF taxa Search</En>
                      <Fr>Recherche de taxons dans GBIF</Fr>
                    </I18n>
                  }
                />
              )}
            />
          </Grid>
          <Grid size={3}>
            <Button
              disabled={disabled || (!SearchValue && !inputValue)}
              startIcon={<ArrowDownward />}
              onClick={() => handleAdd()}
            >
              <I18n>
                <En>Add</En>
                <Fr>Ajouter</Fr>
              </I18n>
            </Button>
          </Grid>

          <Grid container direction="row">
            <Grid size={5}>
              <Grid style={{ margin: "10px" }}>
                <Typography variant="h6">Taxa</Typography>
              </Grid>

              <Box
                border={1}
                borderRadius="4px"
                borderColor="#ababab"
                margin="10px"
              >
                <List>
                  <SortableList
                    items={taxa}
                    onDrop={onDrop}
                    getItemId={getItemId}
                  >
                    {taxa.map((taxaItem, i) => {
                      const taxaId = getItemId(taxaItem, i);
                      return (
                        <SortableItem key={taxaId} id={taxaId}>
                          <ListItem
                            onClick={() => setActiveTaxa(i)}
                            sx={{ cursor: "pointer" }}
                          >
                            <ListItemText
                              primary={
                                <Typography
                                  style={{
                                    fontWeight: activeTaxa === i ? "bold" : "",
                                    width: "80%",
                                  }}
                                >
                                  {taxaItem &&
                                    `${taxaItem.scientificName} (${taxaItem.canonicalName})`}
                                </Typography>
                              }
                            />
                            <ListItemSecondaryAction>
                              <Tooltip
                                title={
                                  <I18n
                                    en="Remove from this record"
                                    fr="Supprimer de cet enregistrement"
                                  />
                                }
                              >
                                <span>
                                  <IconButton
                                    onClick={() => removeItem(i)}
                                    edge="end"
                                    aria-label="clone"
                                    disabled={disabled}
                                  >
                                    <Delete />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </ListItemSecondaryAction>
                          </ListItem>
                        </SortableItem>
                      );
                    })}
                  </SortableList>
                </List>
              </Box>
            </Grid>

            <Grid size={7}>
              <Grid style={{ margin: "10px" }}>
                <Typography variant="h6">Details</Typography>
              </Grid>
              <Box
                border={1}
                borderRadius="4px"
                borderColor="#ababab"
                margin="10px"
                minHeight="48px"
              >
                {taxa[activeTaxa] ? (
                  <TableContainer component={Paper}>
                    <Table size="small" aria-label="simple table">
                      <TableBody>
                        {Object.entries(getActiveTaxaDetails()).map(
                          ([key, value]) => (
                            <TableRow
                              key={key}
                              sx={{
                                "&:last-child td, &:last-child th": {
                                  border: 0,
                                },
                              }}
                            >
                              <TableCell
                                component="th"
                                scope="row"
                                variant="head"
                                align="left"
                                style={{ width: "146px" }}
                              >
                                {capitalize(key)}:
                              </TableCell>
                              <TableCell align="left">{value}</TableCell>
                            </TableRow>
                          ),
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  ""
                )}
              </Box>
            </Grid>
          </Grid>
        </Paper>
      ) : (
        ""
      )}
    </Grid>
  );
};
export default TaxaTab;
